import React, { useState, useEffect } from 'react';
import { FiSave, FiX, FiLayout, FiGrid, FiList, FiMap, FiLayers } from 'react-icons/fi';
import { Menu, Transition, Dialog } from '@headlessui/react';

const VIEW_TYPES = {
  list: { icon: FiList, label: 'List View' },
  grid: { icon: FiGrid, label: 'Grid View' },
  timeline: { icon: FiLayout, label: 'Timeline View' },
  routes: { icon: FiLayers, label: 'Route Planner' },
  map: { icon: FiMap, label: 'Map Focus' },
};

export default function SavedViews({ 
  currentView, 
  onViewChange, 
  currentFilters,
  onLoadView 
}) {
  const [savedViews, setSavedViews] = useState(() => {
    const stored = localStorage.getItem('dispatchSavedViews');
    return stored ? JSON.parse(stored) : [];
  });
  
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [viewName, setViewName] = useState('');

  useEffect(() => {
    localStorage.setItem('dispatchSavedViews', JSON.stringify(savedViews));
  }, [savedViews]);

  const handleSave = () => {
    if (!viewName.trim()) return;
    
    const newView = {
      id: Date.now().toString(),
      name: viewName.trim(),
      filters: { ...currentFilters },
      viewType: currentView,
      createdAt: new Date().toISOString(),
    };
    
    setSavedViews(prev => [...prev, newView]);
    setViewName('');
    setSaveDialogOpen(false);
  };

  const handleDelete = (id) => {
    setSavedViews(prev => prev.filter(v => v.id !== id));
  };

  const handleLoad = (view) => {
    onLoadView(view.filters);
    onViewChange(view.viewType);
  };

  return (
    <>
      <Menu as="div" className="relative">
        <Menu.Button className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-gray-300 dark:hover:border-gray-600 transition-all text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm">
          <FiLayout className="w-4 h-4" />
          <span>Views</span>
        </Menu.Button>

        <Transition
          as={React.Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 focus:outline-none z-50 overflow-hidden">
            <div className="py-1">
              {/* View Type Selector */}
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Layout
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(VIEW_TYPES).map(([key, { icon: Icon, label }]) => (
                    <button
                      key={key}
                      onClick={() => onViewChange(key)}
                      className={`
                        flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors
                        ${currentView === key
                          ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-xs">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Saved Views */}
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Saved Views
                </p>
                {savedViews.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
                    No saved views
                  </p>
                ) : (
                  <div className="space-y-1">
                    {savedViews.map(view => (
                      <div
                        key={view.id}
                        className="flex items-center justify-between group hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg px-2 py-1.5"
                      >
                        <button
                          onClick={() => handleLoad(view)}
                          className="flex-1 text-left text-sm text-gray-700 dark:text-gray-200"
                        >
                          {view.name}
                        </button>
                        <button
                          onClick={() => handleDelete(view.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 transition-opacity"
                        >
                          <FiX className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Save Current View */}
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => setSaveDialogOpen(true)}
                    className={`
                      ${active ? 'bg-gray-50 dark:bg-gray-700' : ''}
                      flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200
                    `}
                  >
                    <FiSave className="w-4 h-4" />
                    Save Current View
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>

      {/* Save Dialog */}
      <Transition show={saveDialogOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setSaveDialogOpen(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Save View
                  </Dialog.Title>
                  
                  <input
                    type="text"
                    value={viewName}
                    onChange={e => setViewName(e.target.value)}
                    placeholder="Enter view name..."
                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none"
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSave();
                      if (e.key === 'Escape') setSaveDialogOpen(false);
                    }}
                    autoFocus
                  />
                  
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={handleSave}
                      className="flex-1 py-2.5 px-4 bg-sky-500 hover:bg-sky-600 text-white rounded-xl font-medium transition-colors"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setSaveDialogOpen(false)}
                      className="flex-1 py-2.5 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
